import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useParams } from "react-router-dom"

import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, Grid, IconButton } from "@mui/material"
import { Menu, MenuItem, NotificationUtils, Tag, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAvatarComponent } from "../../../components/core/KonanAvatar"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"

import styles from "../TrainingData.module.scss"

type ProcessingProps = {
  name: string
  createdAt: string
  createdBy: string
  trainingDataId: string
  type: "Failed" | "Processing"
}

type ParamType = {
  id: string
}

export function ProcessingFailedCard(props: Readonly<ProcessingProps>): React.ReactElement {
  const { name, createdAt, createdBy, trainingDataId, type } = props
  const { id: projectId } = useParams<ParamType>()
  const { t } = useTranslation("TrainingData")

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [deleteTrainingDataDialogOpen, setDeleteTrainingDataDialogOpen] = useState<boolean>(false)

  const deleteTrainingDataMutation = useMutation<AxiosResponse, AxiosError>(
    () => KonanAPI.deleteTrainingDataFromProject(projectId as string, trainingDataId),
    {
      onSuccess: async () => {
        // Invalidate react-query queries
        await queryClient.invalidateQueries(["training-data", projectId])

        const displayName = name || t("notifications.defaultTrainingName")
        NotificationUtils.toast(t("training_data_card.notifications.deleteSuccess", { trainingName: displayName }), {
          severity: "success",
        })
      },
      onError: () => {
        const displayName = name || t("notifications.defaultTrainingName")
        NotificationUtils.toast(t("training_data_card.notifications.deleteError", { trainingName: displayName }), {
          severity: "error",
        })
      },
    },
  )

  const handleClose = (): void => {
    setAnchorEl(null)
  }
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  return (
    <Fragment>
      {deleteTrainingDataDialogOpen && (
        <BaseSimpleDialog
          open={deleteTrainingDataDialogOpen}
          isLoading={deleteTrainingDataMutation.isLoading}
          name={name}
          onAccept={() => deleteTrainingDataMutation.mutateAsync()}
          onClose={() => setDeleteTrainingDataDialogOpen(false)}
          mode={"training-data-deletion"}
        />
      )}

      <Card className="card-box-shadow">
        <Grid container direction="column" className={styles.failedProcessingCard}>
          <Grid item xs={12} direction="row" justifyContent="space-between" container alignItems="flex-start">
            <Grid item xs={10}>
              <Tag size="small" color={type === "Processing" ? "yellow" : "red"}>
                {type === "Processing" ? t("cards.processing.type") : t("cards.failed.type")}
              </Tag>
            </Grid>
            <Grid item xs={2} container justifyContent="flex-end" alignItems="flex-start">
              <IconButton aria-label="settings" onClick={handleClick} size="small" className={styles.headerButton}>
                <MoreHorizIcon />
              </IconButton>
              <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} menuMaxContent>
                <MenuItem
                  onClick={() => {
                    setDeleteTrainingDataDialogOpen(true)
                    handleClose()
                  }}
                >
                  <Typography className={styles.menuItemDelete} variant="a">
                    {t("training_data_card.menu.deleteTrainingData")}
                  </Typography>
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h3-bold" noWrap className={styles.failedProcessingText}>
              {name}
            </Typography>
          </Grid>
          <Grid container item xs={12}>
            <Grid
              container
              item
              spacing={1}
              xs={12}
              alignItems="flex-start"
              className={styles.trainingDataAvatarContainer}
            >
              <KonanAvatarComponent createdBy={createdBy} title={createdBy} createdAt={createdAt} />
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </Fragment>
  )
}
